export interface BaseContentData {
  uuid?: string;
  type?: TypeEnum;
  title?: string;
  title_uuid?: string;
  subtitle?: string;
  subtitle_uuid?: string;
  description?: string;
  description_uuid?: string;
  rich_description?: string;
  rich_description_uuid?: string;
  rich_description_type?: RichDescriptionTypeEnum;
  rich_description_schema?: JSON;
  image_url?: string;
  image_url_uuid?: string;
  image_alt_text?: string;
  image_alt_text_uuid?: string;
  start_at?: Date;
  end_at?: Date;
  display_start_at?: Date;
  display_end_at?: Date;
  display_calendar_icon?: boolean;
  cta_type?: CtaTypeEnum;
  cta_url?: string;
  cta_url_uuid?: string;
  cta_label?: string;
  cta_label_uuid?: string;
  cta_label_at_capacity?: string;
  cta_label_at_capacity_uuid?: string;
  disable_cta_at_capacity?: boolean;
  cta_display_start_at?: Date;
  cta_display_end_at?: Date;
  claimable?: boolean;
  max_claims_total?: number;
  max_claims_per_user?: number;
  claims_reset_interval?: ClaimsResetIntervalEnum;
  rsvp_enabled?: boolean;
  capacity?: number;
  unpublish_at_capacity?: boolean;
  attachable_content_uuid?: string;
  attachable_company_uuid?: string;
  attachable_service_uuid?: string;
  attachable_amenity_uuid?: string;
  attachable_building_service_provider_uuid?: string;
  attachable_service_provider_uuid?: string;
  attach_resource_booking?: boolean;
  attach_capacity_manager?: boolean;
  attach_secondary_content?: boolean;
  attach_community_forum?: boolean;
  attach_office_capacity_manager?: boolean;
  attach_whos_in?: boolean;
  attachable_survey_type?: AttachableSurveyTypeEnum;
  attachable_survey_uuid?: string;
  attach_service_requests?: boolean;
  attachable_resource_uuid?: string;
  attachable_resource_instance_uuid?: string;
  attachable_resource_location_id?: number;
  host_company_uuid?: string;
  host_custom_text?: string;
  host_custom_text_uuid?: string;
  url?: string;
  url_uuid?: string;
  virtual_url?: string;
  virtual_text?: string;
  virtual_text_uuid?: string;
  formatted_address?: string;
  street_number?: string;
  route?: string;
  locality?: string;
  administrative_area_1?: string;
  administrative_area_2?: string;
  postal_code?: string;
  country_code?: string;
  country?: string;
  place_id?: string;
  latitude?: string;
  longitude?: string;
  directions?: string;
  directions_uuid?: string;
  virtual_directions?: string;
  virtual_directions_uuid?: string;
  locale?: string;
  price?: number;
  currency_type?: string;
  building_uuid?: string;
  sponsored_programming?: boolean;
  navigation_type?: NavigationTypeEnum;
  navigation_command?: NavigationCommandData;
  media_title?: string;
  apply_section_to_all_buildings?: boolean;
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
}

export enum TypeEnum {
  EVENT_POST = 'EVENT_POST',
  POST = 'POST',
  OFFER = 'OFFER',
}

export enum NavigationTypeEnum {
  DIRECT_LINK = 'DIRECT_LINK',
  INFORMATIONAL_LINK = 'INFORMATIONAL_LINK',
}

export enum CtaTypeEnum {
  EXTERNAL_URL = 'EXTERNAL_URL',
  DEEP_LINK = 'DEEP_LINK',
  NATIVE_LINK = 'NATIVE_LINK',
  INTERNAL_LINK = 'INTERNAL_LINK',
  MICRO_FRONTEND = 'MICRO_FRONTEND',
  CLAIM = 'CLAIM',
}

export enum RichDescriptionTypeEnum {
  BASIC = 'BASIC',
  CUSTOM = 'CUSTOM',
}

export enum ClaimsResetIntervalEnum {
  PROMOTION = 'PROMOTION',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export enum AttachableSurveyTypeEnum {
  GENERAL = 'GENERAL',
  NPS = 'NPS',
}

export enum NavigationCommandTypeEnum {
  WEB_VIEW = 'WEBVIEW',
  NATIVE_SCREEN = 'NATIVE_SCREEN',
  NATIVE_LINK = 'NATIVE_LINK',
  EXTERNAL_DEEPLINK = 'EXTERNAL_DEEPLINK',
}

export enum NativeScreenValueEnum {
  AMENITIES = 'AMENITIES',
  COMPANIES = 'COMPANIES',
  SECONDARY_CONTENT = 'SECONDARY_CONTENT',
  COMMUNITY_FORUM = 'COMMUNITY_FORUM',
  WHOS_IN = 'WHOS_IN',
}

export interface NavigationCommandOptions {
  url?: string;
  type?: CtaTypeEnum;
  uuid?: string;
  building_uuid?: string;
}

export interface NavigationCommandData {
  type: NavigationCommandTypeEnum;
  value?: NativeScreenValueEnum;
  options?: NavigationCommandOptions;
}

export interface AudienceContentResponse {
  id: number;
  uuid: string;
  audience_uuid: string;
  building_uuid: string;
  category_uuid?: string;
  content_uuid: string;
  promoted_at?: Date;
  weight: number;
  unique_key?: string;
  created_at: Date;
  updated_at: Date;
  deleted_at?: Date;
}

export interface AudienceContentRO {
  audienceContent: Array<AudienceContentResponse>;
}

export interface IncludedTranslatedContentData {
  title?: string;
  subtitle?: string;
  description_title?: string;
  description?: string;
  rich_description?: string;
  rich_description_type?: RichDescriptionTypeEnum;
  rich_description_schema?: JSON;
  image_url?: string;
  image_alt_text?: string;
  cta_url?: string;
  cta_label?: string;
  cta_label_at_capacity?: string;
  host_custom_text?: string;
  url?: string;
  virtual_text?: string;
  directions?: string;
  virtual_directions?: string;
}

export interface BuildingLanguageRO {
  code: string;
  priority: number;
}

export interface BuildingResponse {
  id?: number;
  uuid?: string;
  currency_type?: string;
  languages?: Array<BuildingLanguageRO>;
}

export interface AudienceRO {
  id?: number;
  uuid?: string;
  name?: string;
  building_id?: number;
  description?: string;
  audience_type?: string;
  buildings?: Array<BuildingResponse>;
}

export interface LastRevisionRO {
  author_name: string;
  version_start_at: Date;
}

export interface NotificationAudienceRO {
  audience_uuid: string;
  building_uuid?: string;
  send_notification?: boolean;
}

export enum NotificationAttachmentTypeEnum {
  CONTENT = 'CONTENT',
  MODULE = 'MODULE',
}

export interface NotificationAttachmentRO {
  attachment_type: NotificationAttachmentTypeEnum;
  attachment_uuid?: string;
}

export interface NotificationRO {
  uuid?: string;
  title: string;
  description: string;
  audiences: Array<NotificationAudienceRO>;
  date: Date;
  attachment?: NotificationAttachmentRO;
  is_local_time: boolean;
  author_uuid?: string;
}

export interface NotificationsData {
  is_notification_service_available?: boolean;
  notifications?: NotificationRO[];
}

export interface BuildingToCategoriesRO {
  building_uuid?: string;
  category_uuid?: string;
}

export interface TargetingRO {
  audience_uuids?: Array<string>;
  building_categories?: Array<BuildingToCategoriesRO>;
  building_uuids?: Array<string>;
  category_name?: string;
}

export interface TenantClaimsDetails {
  count?: number;
  user_count?: number;
  status?: string;
  is_claimable?: boolean;
}

export interface ContentData extends BaseContentData {
  id?: number;
  uuid?: string;
  type?: TypeEnum;
  description_title_uuid?: string;
  AudienceContent?: Array<AudienceContentResponse>;
  translations?: Record<string, IncludedTranslatedContentData>;
  audiences?: Array<AudienceRO>;
  targeting?: TargetingRO;
  last_revision?: LastRevisionRO;
  thematic_categories?: string[];
  notification_data?: NotificationsData;
}

/**
 * The expected payload for content data
 */

export interface TenantContentData extends ContentData {
  host_company_name?: string;
  claims_details?: TenantClaimsDetails;
}

export interface ContentResponse {
  data: {
    content: TenantContentData,
  },
}
